import type { StaffMember } from "@kitchen/schemas";
import { Button, Chip, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type RoleRow } from "../api.js";
import { ErrorLine, useAction } from "./common.js";

/** Staff list: the only way anyone becomes staff (ADR-29). Roles are given here or in the role designer. */
export function StaffPage() {
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => api<StaffMember[]>("/staff") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<RoleRow[]>("/roles") });
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const add = useAction(
    async (input: { phone: string; displayName: string; roleId: string }) => {
      const { id } = await api<{ id: string }>("/staff", {
        body: { phone: input.phone, displayName: input.displayName },
      });
      if (input.roleId) await api(`/roles/${input.roleId}/assign`, { body: { personId: id } });
      setPhone("");
      setName("");
    },
    [["staff"], ["roles"]],
  );
  const remove = useAction((id: string) => api(`/staff/${id}/remove`, { body: {} }), [["staff"]]);
  const staffRoles = (roles.data ?? []).filter((r) => r.context === "staff" && r.systemKey !== "support");

  return (
    <div className="grid" style={{ gridTemplateColumns: "minmax(0,1fr) 360px", alignItems: "start" }}>
      <Panel title="کارکنان">
        <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>نام</th>
                <th>موبایل</th>
                <th>PIN</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(staff.data ?? []).map((s) => (
                <tr key={s.id}>
                  <td className="m">
                    <span className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
                      <span>{s.displayName}</span>
                      {s.isSupport && <Chip tone="blue">پشتیبان فنی</Chip>}
                    </span>
                  </td>
                  <td className="num" dir="ltr" style={{ textAlign: "start" }}>
                    {`0${s.phone.slice(3)}`}
                  </td>
                  <td>{s.hasPin ? <Chip tone="ok">ساخته شده</Chip> : <Chip plain>هنوز وارد نشده</Chip>}</td>
                  <td className="end">
                    {s.isSupport ? (
                      <span className="muted t-cap">این دسترسی فقط از سرور تغییر می‌کند</span>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          remove.run(s.id);
                        }}
                        disabled={remove.busy}
                      >
                        حذف از فهرست کارکنان
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ErrorLine error={remove.error} />
      </Panel>
      <Panel title="افزودن به کارکنان">
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            add.run({ phone, displayName: name, roleId });
          }}
        >
          <Field
            id="staff-name"
            label="نام"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Field
            id="staff-phone"
            label="شماره‌ی موبایل"
            dir="ltr"
            inputMode="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
            }}
            hint="با همین شماره از «ورود کارکنان» وارد می‌شود و PIN خودش را می‌سازد."
          />
          <div className="field">
            <label htmlFor="staff-role">نقش</label>
            <select
              id="staff-role"
              className="input"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
              }}
            >
              <option value="">بعداً تعیین می‌کنم</option>
              {staffRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" icon="plus" disabled={add.busy || !name || !phone}>
            افزودن
          </Button>
          <ErrorLine error={add.error} />
        </form>
      </Panel>
    </div>
  );
}
